import React from 'react';
import {
  LogOut,
  User,
  FileText,
  Bell,
  Building2,
  HeadphonesIcon,
  X,
  ArrowRightLeft,
  Percent,
  Shield,
  Zap,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  onClose?: () => void;
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeSection, onSectionChange, onClose, onLogout }) => {
  const { user } = useAuth();
  
  const navigationItems = [
    { id: 'reportes', label: 'Reportes', icon: FileText },
    { id: 'gestion', label: 'Catálogo', icon: Building2 },
    { id: 'clientes', label: 'Gestión de Clientes', icon: User },
    { id: 'atencion', label: 'Atención a Clientes', icon: HeadphonesIcon },
    { id: 'transferencias', label: 'Transferencias Externas', icon: ArrowRightLeft },
    { id: 'mt-center', label: 'MT Center', icon: Zap },
    { id: 'tasas', label: 'Tasas de Interés', icon: Percent },
    { id: 'seguros', label: 'Seguros', icon: Shield },
  ];

  return (
    <div className="w-64 bg-white shadow-soft h-screen flex flex-col">
      {/* Header */}
      <div className="p-4 lg:p-6 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 lg:h-10 lg:w-10">
              <img src="/logo.png" alt="CENTAdmin Logo" className="h-8 w-8 lg:h-10 lg:w-10 rounded-lg" />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-xl font-bold text-secondary-900">CENTAdmin</h1>
              <p className="text-sm text-secondary-500">Sistema de Administración</p>
            </div>
            <div className="lg:hidden">
              <h1 className="text-lg font-bold text-secondary-900">CENTAdmin</h1>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg text-secondary-600 hover:bg-secondary-100"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 lg:p-4 space-y-1 lg:space-y-2">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`w-full flex items-center space-x-3 px-3 lg:px-4 py-2 lg:py-3 rounded-lg text-left transition-colors duration-200 ${
                isActive
                  ? 'bg-[#f0fdfd] text-[#0d9488] border-r-2 border-[#3bbcc8]'
                  : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
              }`}
            >
              <Icon className={`h-4 w-4 lg:h-5 lg:w-5 ${isActive ? 'text-[#3bbcc8]' : 'text-secondary-400'}`} />
              <span className="font-medium text-sm lg:text-base">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="p-3 lg:p-4 border-t border-secondary-200">
        <div className="flex items-center space-x-3 mb-3 lg:mb-4">
          <div className="h-8 w-8 lg:h-10 lg:w-10 bg-secondary-200 rounded-full flex items-center justify-center">
            <User className="h-4 w-4 lg:h-5 lg:w-5 text-secondary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-secondary-900 truncate">
              {user?.email ? user.email.split('@')[0] : 'Usuario Admin'}
            </p>
            <p className="text-xs text-secondary-500 truncate">
              {user?.email || 'admin@centadmin.com'}
            </p>
          </div>
          <button className="p-1 text-secondary-400 hover:text-secondary-600">
            <Bell className="h-4 w-4" />
          </button>
        </div>
        
        <button 
          onClick={onLogout}
          className="w-full flex items-center space-x-3 px-3 lg:px-4 py-2 text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 rounded-lg transition-colors duration-200"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-sm font-medium">Cerrar Sesión</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;

